(function () {
  'use strict';

  /**
   * APO Image Swap (Bont) — vanilla rewrite.
   * Modes (via product tags):
   *   mybont-swap-order  → overlays only (base hidden); LEGACY descending z (first option on top), MODERN ascending (unchanged)
   *   quad-swap-order    → base visible under overlays; ascending z
   *   inline-swap-order  → base visible; descending z (first option on top)
   * Corrected vs the old jQuery engine: official mwApoInit readiness with a
   * bounded poll fallback; targeted listeners (no body-wide observer); no
   * speculative-global nudging; graceful degradation if APO never loads.
   * Speaks both APO generations: modern (mw-apo-container, mw_opts controls)
   * and legacy classic Product Options (mw-options-container, plain selects
   * with data-value-id) — legacy overlays resolve from mwProductOptions config.
   * Overlay stacking order is per-store configurable via window.apoOverlayOrder
   * (Theme settings → APO integration); blank keeps the app's own order.
   */

  var OVERLAY_BASE_Z = 20;
  var POLL_INTERVAL = 500;
  var MAX_ATTEMPTS = 20; // ~10s readiness fallback

  // ---- Pure utilities (unit-tested) --------------------------------------

  function normalizeName(name) {
    if (!name) return '';
    return String(name)
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/( |\/|\\|_)+/g, '-')
      // Unicode-aware: keep letters and numbers from ANY script. The old
      // ASCII-only \w silently erased Korean, Japanese and accented values,
      // collapsing them to an empty slug.
      .replace(/[^\p{L}\p{N}_-]/gu, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function stripShopifyParams(url) {
    if (!url) return '';
    var clean = url.split('?')[0];
    return clean.replace(/_(\d+x\d*|\d*x\d+|\d+x)\.(jpg|jpeg|png|webp|gif|avif)$/i, '.$2');
  }

  function firstFromSrcset(srcset) {
    if (!srcset) return '';
    var first = srcset.split(',')[0].trim();
    return first.split(' ')[0] || '';
  }

  function getOverlayZIndex(mode, index, legacy) {
    if (mode === 'quad') return 10 + index;
    if (mode === 'inline') return OVERLAY_BASE_Z - index;
    // mybont: LEGACY stacks descending (first selected option on top) to match
    // the old Canada theme's APO stacking (z-index 112→12); MODERN keeps its
    // original ascending order, unchanged. quad/other modes stay ascending.
    if (mode === 'mybont' && legacy) return OVERLAY_BASE_Z - index;
    return OVERLAY_BASE_Z + index;
  }

  function injectOnceStyles() {
    if (document.getElementById('apo-image-swap-styles')) return;
    var style = document.createElement('style');
    style.id = 'apo-image-swap-styles';
    style.textContent =
      '.overlay-image{transition:opacity 150ms ease-out;}' +
      '.bont-multi-product-img-container{transition:opacity 120ms ease-out;}';
    document.head.appendChild(style);
  }

  function ensureRelative(el) {
    if (!el) return;
    var pos = window.getComputedStyle(el).position;
    if (!pos || pos === 'static') el.style.position = 'relative';
  }

  var COLOUR_WORDS = /(red|blue|green|black|white|purple|lavender|pink|yellow|orange|brown|gray|grey|silver|gold|navy|teal|cyan|magenta|lime|olive|maroon|aqua|violet|indigo|turquoise|coral|salmon|khaki|tan|beige|ivory|cream|mint|sage|rose|wine|burgundy|crimson|azure|emerald|amber|bronze|copper|platinum|pearl)/i;

  function isColourOptionName(name) {
    var n = String(name || '').toLowerCase();
    return n.indexOf('color') !== -1 || n.indexOf('colour') !== -1;
  }

  /**
   * Does a change on the control with this `name` mean the swapped image must
   * be re-rendered? Deliberately free of any translated text.
   *
   * Horizon builds its picker names from the OPTION NAME — `Color-<id>-swatch`
   * (variant-swatches.liquid), `Color-<block>-<id>` (variant-main-picker.liquid)
   * and `options[Color]` — and Translate & Adapt translates that name. Matching
   * "color" inside it, as this engine used to, bound a listener on the English
   * storefront and NOTHING on a translated one, so the colour swatch moved no
   * image until an APO option was touched.
   *
   * The locale-stable signals instead:
   *   - `apo-option-<index>`: the variant shim in apo-hooks.liquid, named by
   *     position. It mirrors every Horizon variant change and dispatches a
   *     bubbling `change` AFTER it has applied the new value, so a render
   *     queued from it always reads fresh state.
   *   - `mw_opts…`: APO's own option controls (modern generation).
   *   - `options[`: the native variant <select>; only the prefix is matched,
   *     because the option name inside the brackets is translated.
   */
  function isSwapTrigger(name) {
    if (!name) return false;
    var n = String(name);
    if (/^apo-option-\d+$/.test(n)) return true;
    if (n.indexOf('mw_opts') === 0) return true;
    if (n.indexOf('options[') === 0) return true;
    return false;
  }

  /**
   * The default-locale name of an option in a /products/{handle}.js payload.
   * Handles both payload shapes: objects ({name, position, values}) and the
   * older names-only array. Returns '' when unavailable.
   */
  function canonicalOptionName(product, optionIndex) {
    if (!product || !product.options) return '';
    var option = product.options[optionIndex];
    if (!option) return '';
    return typeof option === 'string' ? option : (option.name || '');
  }

  /**
   * The default-locale values of an option, in the option's own order. When the
   * payload carries names only, distinct values are recovered from the variants
   * via option1/option2/option3, preserving first-seen order.
   */
  function canonicalOptionValues(product, optionIndex) {
    if (!product || !product.options || !product.options[optionIndex]) return [];
    var option = product.options[optionIndex];
    if (option && option.values && option.values.length) return option.values.slice();

    var key = 'option' + (optionIndex + 1);
    var variants = product.variants || [];
    var seen = {};
    var values = [];
    for (var i = 0; i < variants.length; i++) {
      var value = variants[i] && variants[i][key];
      // Guard the prototype chain: a value of "constructor" would otherwise
      // read as already-seen.
      if (value && !Object.prototype.hasOwnProperty.call(seen, value)) {
        seen[value] = true;
        values.push(value);
      }
    }
    return values;
  }

  /**
   * Resolve the selected colour from the canonical (default-locale) payload
   * using the shim's locale-stable indices. Translate & Adapt translates option
   * values in place, so value index N is the same value in every language —
   * that is the only crossing point between the translated page and the English
   * alt text the base layers carry. Returns '' when it cannot be resolved.
   */
  function canonicalColour(product, candidates) {
    if (!product || !candidates) return '';
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c || c.kind !== 'shim') continue;
      // NaN fails both comparisons, which is what we want for absent attributes.
      if (!(c.optionIndex >= 0) || !(c.valueIndex >= 0)) continue;
      if (!isColourOptionName(canonicalOptionName(product, c.optionIndex))) continue;
      var values = canonicalOptionValues(product, c.optionIndex);
      // A legacy payload's values are reconstructed from first-seen variant
      // order, which only matches the declared option-value order when the
      // variant matrix is complete. A deleted/never-created combination
      // shortens and shifts that reconstruction, so valueIndex (taken from
      // the shim, which counts the full declared list) would land on a
      // real-looking but wrong value. A length mismatch means the canonical
      // list cannot be trusted to align index-for-index with the shim, so
      // refuse the mapping rather than guess.
      if (c.valueCount >= 0 && values.length !== c.valueCount) continue;
      var value = values[c.valueIndex];
      if (value) return normalizeName(value);
    }
    return '';
  }

  /**
   * Resolve a normalised colour from ordered candidate inputs (DOM-agnostic so it
   * can be unit-tested). Candidates are gathered by resolveColour() in priority
   * order; the first one that reads as a colour wins. Each candidate:
   *   { kind: 'named-colour', value }                      // native Color radio (name already identifies it)
   *   { kind: 'select', value, text }                      // native colour <select> (prefer option text)
   *   { kind: 'shim', optionName, value,
   *     optionIndex, valueIndex, valueCount }               // APO variant shim wrap (colour iff optionName is a colour).
   *                                                          // optionIndex/valueIndex are the shim's locale-stable
   *                                                          // position, used by canonicalColour() to bridge a
   *                                                          // translated value via canonicalProduct; valueCount is
   *                                                          // the option's own radio count, used to reject a
   *                                                          // canonical values list that reconstructs to a
   *                                                          // different length. All three are optional — absent
   *                                                          // (NaN/undefined) means the bridge is skipped for this
   *                                                          // candidate and it falls back to the text heuristic.
   *   { kind: 'generic-option', name, value }              // native options[...] radio (colour iff name/value looks like one)
   *
   * canonicalProduct is the optional /products/{handle}.js payload in the
   * store's default locale (or null/undefined). When present, it is checked
   * first via canonicalColour() against every 'shim' candidate, before any
   * text heuristic runs — see that function's own doc for why.
   */
  function resolveColourFromCandidates(candidates, canonicalProduct) {
    // The canonical bridge outranks every text heuristic: on a translated page
    // the on-page strings are unusable, and after the Unicode fix they would
    // otherwise resolve to a translated slug that no English alt can match.
    var bridged = canonicalColour(canonicalProduct, candidates);
    if (bridged) return bridged;
    if (!candidates) return '';
    for (var i = 0; i < candidates.length; i++) {
      var c = candidates[i];
      if (!c) continue;
      var resolved = '';
      if (c.kind === 'named-colour') {
        resolved = normalizeName(c.value);
      } else if (c.kind === 'select') {
        resolved = normalizeName(c.text || c.value);
      } else if (c.kind === 'shim') {
        if (isColourOptionName(c.optionName)) resolved = normalizeName(c.value);
      } else if (c.kind === 'generic-option') {
        if (isColourOptionName(c.name) || COLOUR_WORDS.test(c.value || '')) {
          resolved = normalizeName(c.value);
        }
      }
      // A candidate that normalises to nothing (e.g. a translated value under
      // ASCII-only slug rules) must not end the search — later candidates may
      // still carry a usable value.
      if (resolved) return resolved;
    }
    return '';
  }

  // ---- Engine ------------------------------------------------------------
  // Guard the DOM engine so the file can also be required under Node (for the
  // pure-utility unit tests) where `window`/`document` do not exist.
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {

  /**
   * Pick a value's overlay layer from its config images. Overlay options carry
   * two images: images[0] is the square JPG swatch (used only as the option
   * preview) and a transparent PNG is the actual layer that composes the
   * product image. Prefer the PNG; fall back to the first image when none.
   */
  function pickOverlayImage(images) {
    if (!images || !images.length) return '';
    // images[0] is the swatch/preview shown in the option selector; the overlay
    // LAYER that composes the product is the last image (the swatch can be a JPG
    // OR a PNG, so position — not format — distinguishes them; single-image
    // values are the layer). Walk from the end and take the last transparent
    // image, so a JPG swatch is never stacked opaque over the composite.
    for (var i = images.length - 1; i >= 0; i--) {
      var url = images[i] && images[i].url;
      if (url && /\.(png|webp|gif|avif)(\?|$)/i.test(url)) return url;
    }
    return '';
  }

  /**
   * Legacy classic Product Options: the app renders no per-value media in the
   * DOM, so a value's configured image is resolved from the app's own config
   * global instead. Only options set to image_mode 'overlay' compose the image
   * — options set to 'disable' (colour swatch pickers, non-visual components,
   * mybont frame/wheel, etc.) must NOT stack onto the gallery, matching the
   * classic app. Returns '' when unavailable or disabled.
   */
  function legacyOverlaySrc(valueId) {
    try {
      var options = window.mwProductOptions.Module.config.options;
      for (var key in options) {
        var option = options[key];
        var values = option && option.values;
        var value = values && values[valueId];
        if (value) {
          if (option.image_mode === 'disable') return '';
          return pickOverlayImage(value.images);
        }
      }
    } catch (e) {}
    return '';
  }

  /**
   * Parse the theme's overlay stacking order setting (window.apoOverlayOrder,
   * a comma-separated list of APO option names) into normalised entries.
   * Blank/unset → [] → overlays keep the app's own option order.
   */
  function parseOverlayOrder(raw) {
    if (!raw) return [];
    return String(raw)
      .split(',')
      .map(function (s) { return s.toLowerCase().replace(/\s+/g, ''); })
      .filter(function (s) { return s.length > 0; });
  }

  /**
   * Normalised group label (e.g. "frame", "wheels") for a selected APO value
   * id, or '' when it cannot be resolved. Mirrors the label lookup used by
   * the non-visual group skip in renderOverlays (deliberately duplicated —
   * that inline lookup is live on bont.com and stays untouched).
   */
  function overlayOptionLabel(valueId) {
    var valueEl = document.querySelector(
      'option[value="' + valueId + '"], option[data-value-id="' + valueId + '"], input[type="radio"][name^="mw_opts"][value="' + valueId + '"]'
    );
    var optionBlock = valueEl ? valueEl.closest('.mw-option') : null;
    var labelEl = optionBlock ? optionBlock.querySelector('.mw-text--style-strong, .mw_product_option_label') : null;
    return labelEl ? labelEl.textContent.toLowerCase().replace(/\s+/g, '') : '';
  }

  window.apoImageSwap = {
    mode: null,        // 'mybonts' | 'quad' | 'inline' | null
    overlayOrder: [],  // parsed apo_overlay_order setting (normalised names)
    baseLayers: [],    // NodeList → array of .bont-multi-product-img-container
    host: null,        // .bont-product-media-container
    ready: false,
    renderQueued: false,
    canonicalProduct: null,

    init: function () {
      this.host = document.querySelector('.bont-product-media-container');
      if (!this.host) return; // not an image-swap product page
      this.baseLayers = Array.prototype.slice.call(
        document.querySelectorAll('.bont-multi-product-img-container')
      );

      // Mode is resolved once in Liquid (snippets/apo-product-mode.liquid) and
      // emitted as window.apoSwapMode by the image-swap gallery snippet.
      this.mode = window.apoSwapMode || null;

      // Optional per-store stacking order (Theme settings → APO integration).
      this.overlayOrder = parseOverlayOrder(window.apoOverlayOrder);

      injectOnceStyles();
      this.loadCanonical();
      this.whenApoReady(this.onReady.bind(this));
    },

    /**
     * On a non-primary locale, fetch the product in its default locale so the
     * translated selection can be mapped back to the values the base-layer alt
     * text uses. Never blocks rendering: until it lands (or if it never does)
     * the engine behaves exactly as it did before.
     */
    loadCanonical: function () {
      if (window.apoLocaleIsPrimary) return;
      var url = window.apoCanonicalProductUrl;
      if (!url || typeof window.fetch !== 'function') return;
      var self = this;
      window.fetch(url, { credentials: 'same-origin' })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (data) {
          if (!data || !data.options) return;
          self.canonicalProduct = data;
          if (self.ready) self.queueRender();
        })
        .catch(function () {});
    },

    /** Resolve readiness via the official bus AND a bounded poll; fire once. */
    whenApoReady: function (cb) {
      var self = this;
      var done = false;
      function fire() { if (done) return; done = true; cb(); }

      // Official bus: chain onto any existing onEvent without clobbering it.
      window.mwApoInit = window.mwApoInit || {};
      var prev = typeof window.mwApoInit.onEvent === 'function' ? window.mwApoInit.onEvent : null;
      window.mwApoInit.onEvent = function (event, args) {
        if (prev) { try { prev(event, args); } catch (e) {} }
        if (event === 'init::finish') fire();
      };

      // Bounded poll fallback (and the only signal if the bus never calls).
      var attempts = 0;
      var poll = setInterval(function () {
        attempts++;
        if (document.querySelector('.mw-apo-container, .mw-options-container') || attempts >= MAX_ATTEMPTS) {
          clearInterval(poll);
          fire();
        }
      }, POLL_INTERVAL);
    },

    onReady: function () {
      this.ready = true;
      ensureRelative(this.host);
      this.bindEvents();
      this.watchApoDom();
      this.render();
    },

    bindEvents: function () {
      var queue = this.queueRender.bind(this);

      // One delegated listener for every control that can change the image.
      // Delegated rather than bound per element so it also survives Horizon
      // morphing the picker (and the shim) on a variant change — see
      // assets/variant-picker.js.
      document.addEventListener('change', function (e) {
        var t = e.target;
        if (!t) return;
        if (isSwapTrigger(t.getAttribute && t.getAttribute('name'))) {
          queue();
          return;
        }
        // Legacy classic Product Options: matched by class, as those controls
        // carry no name we can key on.
        if (t.matches && t.matches('.mw-option__control, .mw-options-container select')) {
          queue();
        }
      });

      // Horizon's own variant event, and the only signal on a product whose
      // shim is absent. It fires before apo-hooks applies the new values, but
      // queueRender defers to the next frame — by which time the shim is in
      // sync — and coalesces this with the shim's own change event.
      document.addEventListener('variant:update', queue);
    },

    /** Scoped observer on the APO container only (not body-wide). */
    watchApoDom: function () {
      var apo = document.querySelector('.mw-apo-container, .mw-options-container');
      if (!apo || typeof MutationObserver === 'undefined') return;
      var self = this;
      var t;
      var observer = new MutationObserver(function () {
        clearTimeout(t);
        t = setTimeout(function () { self.queueRender(); }, 50);
      });
      observer.observe(apo, { childList: true, subtree: true, attributes: true });
    },

    queueRender: function () {
      if (this.renderQueued) return;
      this.renderQueued = true;
      var self = this;
      requestAnimationFrame(function () {
        self.renderQueued = false;
        self.render();
      });
    },

    render: function () {
      this.renderBase();
      this.renderOverlays();
    },

    /** Show the base layer matching the selected colour (mode-aware). */
    renderBase: function () {
      var i;
      if (this.mode === 'mybonts') {
        for (i = 0; i < this.baseLayers.length; i++) {
          this.baseLayers[i].style.opacity = '0';
          this.baseLayers[i].style.zIndex = '0';
        }
        return;
      }

      var wanted = this.resolveColour();

      // Graceful degradation: no colour resolved yet → show the first layer.
      if (!wanted) {
        for (i = 0; i < this.baseLayers.length; i++) {
          var first = i === 0;
          this.baseLayers[i].style.opacity = first ? '1' : '0';
          this.baseLayers[i].style.zIndex = first ? '1' : '0';
        }
        return;
      }

      var winner = null;
      var la, lf, im;

      // LEGACY packages share ONE alt (the product title) across most images,
      // and the title CONTAINS the colour word — so a plain "contains" match
      // hits every image and the first (a lifestyle shot) wins. The merchant
      // marks the configurator's clean base image explicitly: its alt IS the
      // colour (e.g. alt="Lavender") or its filename starts with the colour
      // (e.g. black_….png). So try those exact markers first, in priority
      // order, before the loose "contains" fallback. Legacy only — modern base
      // selection is unchanged.
      if (document.querySelector('.mw-product-options--legacy')) {
        // 1) alt is exactly the colour
        for (i = 0; i < this.baseLayers.length && !winner; i++) {
          if (normalizeName(this.baseLayers[i].getAttribute('data-bont-alt') || '') === wanted) winner = this.baseLayers[i];
        }
        // 2) filename starts with the colour
        for (i = 0; i < this.baseLayers.length && !winner; i++) {
          im = this.baseLayers[i].querySelector('img');
          lf = im ? normalizeName(((im.getAttribute('src') || '').split('/').pop().split('?')[0]).replace(/\.[a-z0-9]+$/i, '')) : '';
          if (lf && (lf === wanted || lf.indexOf(wanted + '-') === 0)) winner = this.baseLayers[i];
        }
        // 3) alt starts with the colour
        for (i = 0; i < this.baseLayers.length && !winner; i++) {
          la = normalizeName(this.baseLayers[i].getAttribute('data-bont-alt') || '');
          if (la && la.indexOf(wanted + '-') === 0) winner = this.baseLayers[i];
        }
      }

      // Fallback (all generations): alt contains the colour, else the first layer.
      for (i = 0; i < this.baseLayers.length && !winner; i++) {
        la = normalizeName(this.baseLayers[i].getAttribute('data-bont-alt') || '');
        if (la && (la === wanted || la.indexOf(wanted + '-') === 0 || la.indexOf(wanted) !== -1)) winner = this.baseLayers[i];
      }
      if (!winner) winner = this.baseLayers[0] || null;

      for (i = 0; i < this.baseLayers.length; i++) {
        var isWinner = this.baseLayers[i] === winner;
        this.baseLayers[i].style.opacity = isWinner ? '1' : '0';
        this.baseLayers[i].style.zIndex = isWinner ? (this.mode === 'quad' ? '5' : '1') : '0';
      }
    },

    /** Read the currently selected colour value and normalise it. */
    resolveColour: function () {
      var candidates = [];

      // 1. Native Horizon colour radios (exact then fuzzy name match).
      var el = document.querySelector('input[type="radio"][name="Color"]:checked')
            || document.querySelector('input[type="radio"][name*="color" i]:checked');
      if (el) candidates.push({ kind: 'named-colour', value: el.value });

      // 2. Native colour <select>.
      var sel = document.querySelector('select[name="Color"], select[name*="color" i]');
      if (sel && sel.value) {
        var opt = sel.options[sel.selectedIndex];
        candidates.push({ kind: 'select', value: sel.value, text: (opt && opt.textContent) || '' });
      }

      // 3. APO variant shim. Single-value colours (or colours exposed only via
      //    APO) never render a native name="Color" input — they reach the DOM
      //    solely through the shim, whose radios are named `apo-option-N` but
      //    whose wrap carries the real option name in `data-option-name`. Reading
      //    it here fixes products (e.g. the roller-derby skate) whose base image
      //    otherwise fell back to the first (hero) gallery layer.
      var wraps = document.querySelectorAll('.variant-input-wrap[data-option-name]');
      for (var w = 0; w < wraps.length; w++) {
        var checked = wraps[w].querySelector('input[type="radio"]:checked');
        if (checked) {
          candidates.push({
            kind: 'shim',
            optionName: wraps[w].getAttribute('data-option-name') || '',
            optionIndex: parseInt(wraps[w].getAttribute('data-option-index'), 10),
            valueIndex: parseInt(checked.getAttribute('data-value-index'), 10),
            // Count of the option's own radios, so the bridge can refuse a
            // canonical values list that reconstructs to a different length
            // (see canonicalColour) instead of trusting a shifted index.
            valueCount: wraps[w].querySelectorAll('input[type="radio"]').length,
            value: checked.value
          });
        }
      }

      // 4. Generic native options[...] radios that look like a colour.
      var radios = document.querySelectorAll('input[type="radio"][name^="options["]:checked');
      for (var i = 0; i < radios.length; i++) {
        candidates.push({
          kind: 'generic-option',
          name: radios[i].getAttribute('name') || '',
          value: radios[i].value || ''
        });
      }

      return resolveColourFromCandidates(candidates, this.canonicalProduct);
    },

    /** Build/update overlay <img> nodes for selected APO options (diffing). */
    renderOverlays: function () {
      if (!this.host) return;

      var desired = [];
      var seen = {};
      document.querySelectorAll(
        'select[name^="mw_opts"] option:checked, .mw-option__control option:checked, input[type="radio"][name^="mw_opts"]:checked'
      ).forEach(function (el) {
        var v = el.value;
        if (v && v.indexOf('v_') === 0 && !seen[v]) { seen[v] = true; desired.push(v); }
      });

      // Legacy generation (classic Product Options): plain selects inside
      // .mw-options-container carry the value id in data-value-id and the
      // display text in value. Skip the app's conditionally hidden values.
      document.querySelectorAll('.mw-options-container select option:checked').forEach(function (el) {
        if (el.disabled || el.getAttribute('data-hidden') === '1') return;
        var v = el.getAttribute('data-value-id') || '';
        if (v.indexOf('value_') === 0 && !seen[v]) { seen[v] = true; desired.push(v); }
      });

      // Optional per-store stacking order: treat the selected options as if
      // the app listed them in the configured order (options not named follow,
      // in the app's own order). Blank setting → empty list → no reordering.
      var order = this.overlayOrder;
      if (order.length && desired.length > 1) {
        desired = desired
          .map(function (valueId, i) {
            var label = overlayOptionLabel(valueId);
            var rank = order.length;
            for (var r = 0; r < order.length; r++) {
              if (label && label.indexOf(order[r]) !== -1) { rank = r; break; }
            }
            return { valueId: valueId, i: i, rank: rank };
          })
          .sort(function (a, b) { return a.rank - b.rank || a.i - b.i; })
          .map(function (d) { return d.valueId; });
      }

      var existing = {};
      this.host.querySelectorAll('.overlay-image').forEach(function (img) {
        var id = img.getAttribute('data-value-id');
        if (id) existing[id] = img;
      });

      var self = this;
      desired.forEach(function (valueId, index) {
        // Skip non-visual groups (e.g. "Bearing") — match the OPTION's own group
        // label. Scope to the `.mw-option` block (which holds the label); the old
        // closest('.mw-stack--vertical') landed on the control wrapper, so it read
        // the first <option> text ("-- Please Select --") and never matched.
        var valueEl = document.querySelector(
          'option[value="' + valueId + '"], option[data-value-id="' + valueId + '"], input[type="radio"][name^="mw_opts"][value="' + valueId + '"]'
        );
        var optionBlock = valueEl ? valueEl.closest('.mw-option') : null;
        if (optionBlock) {
          var labelEl = optionBlock.querySelector('.mw-text--style-strong, .mw_product_option_label');
          var labelText = labelEl ? labelEl.textContent.toLowerCase().replace(/\s+/g, '') : '';
          if (labelText.indexOf('bearing') !== -1) {
            return;
          }
        }

        var z = getOverlayZIndex(self.mode, index, valueId.indexOf('value_') === 0);

        if (existing[valueId]) {
          existing[valueId].style.zIndex = String(z);
          delete existing[valueId];
          return;
        }

        // Overlay source. Modern APO renders per-value media below the option;
        // the legacy classic app keeps value images in its config global
        // instead (no DOM media), so legacy value_… ids resolve from there.
        var src = '';
        if (valueId.indexOf('value_') === 0) {
          src = legacyOverlaySrc(valueId);
        } else {
          var media = document.querySelector('.mw-option__media[data-value-id="' + valueId + '"]:not(.mw-hidden)');
          if (!media || !media.closest('.mw-stack__item:not(.mw-hidden)')) return;
          var img = media.querySelector('img');
          var source = media.querySelector('source');
          src = (img && (img.getAttribute('src') || img.getAttribute('data-src')))
             || (source && firstFromSrcset(source.getAttribute('srcset')))
             || '';
        }
        if (!src) return;
        src = stripShopifyParams(src);

        var overlay = document.createElement('img');
        overlay.className = 'overlay-image';
        overlay.setAttribute('data-value-id', valueId);
        overlay.alt = '';
        overlay.decoding = 'async';
        overlay.style.zIndex = String(z);
        overlay.style.opacity = '0';
        overlay.style.pointerEvents = 'none';
        overlay.onload = function () { overlay.style.opacity = '1'; };
        overlay.src = src;
        ensureRelative(self.host);
        self.host.appendChild(overlay);
      });

      // Remove overlays no longer selected.
      Object.keys(existing).forEach(function (id) {
        var img = existing[id];
        img.style.opacity = '0';
        var removed = false;
        function cleanup() { if (removed) return; removed = true; if (img.parentNode) img.parentNode.removeChild(img); }
        img.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 300);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { window.apoImageSwap.init(); });
  } else {
    window.apoImageSwap.init();
  }

  } // end DOM engine guard

  // CommonJS export shim (for node --test; harmless in browser).
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { normalizeName: normalizeName, stripShopifyParams: stripShopifyParams, firstFromSrcset: firstFromSrcset, getOverlayZIndex: getOverlayZIndex, isColourOptionName: isColourOptionName, isSwapTrigger: isSwapTrigger, resolveColourFromCandidates: resolveColourFromCandidates, canonicalOptionName: canonicalOptionName, canonicalOptionValues: canonicalOptionValues };
  }
})();
